import { useEffect, useState } from "react";
import { Alert, Input, Modal, Select, Table, Upload } from "antd";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  addWatchlistBvn,
  getWatchlist,
  getWatchlistSummary,
  removeWatchlistBvn,
  uploadWatchlistCsv,
} from "../../api/bvnWatchlist";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { usePermissionEffect } from "../../utils/usePermissionEffect";
import CustomerLink from "../shared/CustomerLink";
import NoAccess from "../shared/NoAccess";

const TEMPLATE = "bvn,name,reason\n22212345678,John Doe,Reported by bank\n";

const downloadTemplate = () => {
  const url = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "bvn-watchlist-template.csv";
  a.click();
  URL.revokeObjectURL(url);
};

const btn = "rounded-[5px] px-4 py-2 font-bold text-[14px] disabled:opacity-50 whitespace-nowrap";

const columns = ({ canDelete, onRemove }) =>
  [
    {
      title: "BVN",
      dataIndex: "bvn",
      key: "bvn",
      render: (v) => <span className="font-mono">{v}</span>,
    },
    {
      title: "Name / reason",
      key: "name",
      render: (_, r) => (
        <div>
          <div>{r.name || "—"}</div>
          {r.reason && <div className="text-[12px] text-[#00000080]">{r.reason}</div>}
        </div>
      ),
    },
    {
      title: "On our system",
      key: "onSystem",
      render: (_, r) =>
        r.onSystem ? (
          <div className="space-y-1">
            <span className="bg-[#EB57571A] text-[#EB5757] rounded-[3px] px-[10px] py-[3px] text-[13px]">
              Yes
            </span>
            {r.users.map((u) => (
              <div key={u.userId} className="text-[13px]">
                <CustomerLink userId={u.userId} record={{ user: u }} />
                <span className="text-[12px] text-[#00000080]">
                  {" "}
                  {u.flagged ? "· flagged & deactivated" : "· not flagged yet"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <span className="bg-[#0000000D] text-[#4F4F4F] rounded-[3px] px-[10px] py-[3px] text-[13px]">
            No
          </span>
        ),
    },
    {
      title: "Added",
      key: "added",
      render: (_, r) => (
        <div className="text-[13px]">
          <div>{new Date(r.createdAt).toLocaleDateString()}</div>
          <div className="text-[12px] text-[#00000080]">
            {{ csv: "CSV", nibss: "NIBSS (auto)" }[r.source] || "Manual"} · {r.addedBy || "—"}
          </div>
        </div>
      ),
    },
    canDelete && {
      title: "",
      key: "action",
      render: (_, r) => (
        <button type="button" className="text-[#EB5757] text-[13px]" onClick={() => onRemove(r)}>
          Remove
        </button>
      ),
    },
  ].filter(Boolean);

const BvnWatchlist = () => {
  const queryClient = useQueryClient();
  const { getModulePermission } = usePermissionEffect();
  const canView = getModulePermission("Watchlist", "canView");
  const canEdit = getModulePermission("Watchlist", "canCreate");
  const canDelete = getModulePermission("Watchlist", "canDelete");

  const [page, setPage] = useState(1);
  const [onSystem, setOnSystem] = useState();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ bvn: "", name: "", reason: "" });
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isFetching } = useQuery(
    ["bvn-watchlist", page, onSystem, search],
    () => getWatchlist({ page, limit: 20, onSystem, search }),
    { keepPreviousData: true, enabled: canView },
  );
  const { data: summary } = useQuery("bvn-watchlist-summary", getWatchlistSummary, { enabled: canView });

  const refresh = () => {
    queryClient.invalidateQueries("bvn-watchlist");
    queryClient.invalidateQueries("bvn-watchlist-summary");
    queryClient.invalidateQueries("customers");
  };

  const add = useMutation(addWatchlistBvn, {
    onSuccess: (r) => {
      showSuccessToast(r?.message || "BVN added");
      setAddOpen(false);
      setForm({ bvn: "", name: "", reason: "" });
      refresh();
    },
    onError: (e) => showErrorToast(e.message),
  });

  const upload = useMutation(uploadWatchlistCsv, {
    onSuccess: (r) => {
      setUploadResult(r?.data);
      showSuccessToast(r?.message || "CSV processed");
      refresh();
    },
    onError: (e) => showErrorToast(e.message),
  });

  const remove = useMutation(removeWatchlistBvn, {
    onSuccess: () => {
      showSuccessToast("BVN removed from the watchlist");
      refresh();
    },
    onError: (e) => showErrorToast(e.message),
  });

  const onRemove = (r) =>
    Modal.confirm({
      title: `Remove ${r.bvn} from the watchlist?`,
      content: r.onSystem
        ? "Flagged customers stay deactivated. Reactivate them from their customer page after review."
        : undefined,
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: () => remove.mutate(r._id),
    });

  const bvnValid = /^\d{11}$/.test(form.bvn);

  const cards = [
    ["BVNs on watchlist", summary?.total ?? 0],
    ["On our system", summary?.onSystem ?? 0],
    ["Not on our system", summary?.notOnSystem ?? 0],
    ["Accounts flagged", summary?.flaggedAccounts ?? 0],
  ];

  if (!canView) return <NoAccess what="the BVN watchlist" />;

  return (
    <div className="w-full h-full pt-5 px-5 pb-40 space-y-6 font-dmsans">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-bold text-[22px] text-[#070E05]">BVN Watchlist</div>
          <div className="text-[13px] text-[#00000080]">
            Customers whose BVN is on this list are deactivated automatically and compliance is emailed.
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-3">
            <button type="button" onClick={downloadTemplate} className={`text-dark bg-[#0000000D] ${btn}`}>
              CSV template
            </button>
            <Upload
              accept=".csv,text/csv"
              showUploadList={false}
              beforeUpload={(file) => {
                upload.mutate(file);
                return false;
              }}
            >
              <button type="button" disabled={upload.isLoading} className={`text-dark bg-[#0000000D] ${btn}`}>
                {upload.isLoading ? "Uploading…" : "Upload CSV"}
              </button>
            </Upload>
            <button type="button" onClick={() => setAddOpen(true)} className={`bg-dark text-white ${btn}`}>
              Add BVN
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-4">
        {cards.map(([title, value]) => (
          <div key={title} className="bg-white rounded-lg px-[28px] py-[18px]">
            <div className="text-[12px] text-[#00000080]">{title}</div>
            <div className="text-[22px] font-bold text-[#14142B] mt-2">
              {Number(value).toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      {uploadResult && (
        <Alert
          type={uploadResult.summary.invalid ? "warning" : "success"}
          closable
          onClose={() => setUploadResult(null)}
          message={`CSV processed: ${uploadResult.summary.added} added, ${uploadResult.summary.alreadyListed} already listed, ${uploadResult.summary.duplicatesInFile} duplicate rows, ${uploadResult.summary.invalid} invalid. ${uploadResult.summary.flaggedAccounts} account(s) flagged.`}
          description={
            uploadResult.invalid?.length ? (
              <ul className="text-[12px] list-disc pl-5 max-h-[160px] overflow-y-auto">
                {uploadResult.invalid.map((x) => (
                  <li key={`${x.line}-${x.value}`}>
                    Line {x.line}: "{x.value}" – {x.error}
                  </li>
                ))}
              </ul>
            ) : null
          }
        />
      )}

      <div className="flex flex-col gap-4 bg-white rounded-lg p-5">
        <div className="flex justify-between items-center gap-4">
          <div className="font-bold text-[18px] text-[#070E05]">Watchlisted BVNs</div>
          <div className="flex gap-3">
            <Select
              allowClear
              placeholder="On our system?"
              value={onSystem}
              onChange={(v) => {
                setOnSystem(v);
                setPage(1);
              }}
              className="w-[180px]"
              options={[
                { value: "true", label: "On our system" },
                { value: "false", label: "Not on our system" },
              ]}
            />
            <Input.Search
              allowClear
              placeholder="BVN, name or reason"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-[260px]"
            />
          </div>
        </div>
        {data?.warning && <Alert type="warning" showIcon message={data.warning} />}
        <Table
          rowKey="_id"
          loading={isFetching}
          dataSource={data?.entries || []}
          columns={columns({ canDelete, onRemove })}
          pagination={{
            current: page,
            pageSize: 20,
            total: data?.total || 0,
            showSizeChanger: false,
            onChange: setPage,
          }}
        />
      </div>

      <Modal
        title="Add BVN to watchlist"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        okText={add.isLoading ? "Adding…" : "Add BVN"}
        okButtonProps={{ disabled: !bvnValid || add.isLoading }}
        onOk={() => add.mutate(form)}
      >
        <div className="space-y-3 py-2">
          <div>
            <div className="text-[13px] mb-1">BVN</div>
            <Input
              placeholder="11 digits"
              value={form.bvn}
              maxLength={11}
              onChange={(e) => setForm((f) => ({ ...f, bvn: e.target.value.replace(/\D/g, "") }))}
              status={form.bvn && !bvnValid ? "error" : undefined}
            />
          </div>
          <div>
            <div className="text-[13px] mb-1">Name (optional)</div>
            <Input value={form.name} maxLength={120} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <div className="text-[13px] mb-1">Reason (optional)</div>
            <Input.TextArea
              rows={2}
              value={form.reason}
              maxLength={300}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            />
          </div>
          <div className="text-[12px] text-[#00000080]">
            If a customer already has this BVN, their account is deactivated straight away and compliance is emailed.
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BvnWatchlist;
